import React, { useCallback, useMemo, useState } from "react";
import { ChevronDownIcon } from "lucide-react";
import { useProject } from "../../../data/project";
import { useCurrentOrgUser } from "../../../auth/org-provider";
import { UserAvatarAndName } from "../../../components/shared/org/avatar-for-user";
import { ProjectWorkstreamIcon } from "../../../components/shared/project/workstream-header";
import { DeliverableIcon } from "../../../components/shared/project/deliverable-card";
import { OwnerSelector } from "../../../components/shared/project/owner-selector";
import { Badge } from "../../../components/ui/badge";
import { Progress } from "../../../components/ui/progress";
import { Button } from "../../../components/ui/button";
import Spinner from "../../../components/ui/spinner";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../../../components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../../components/ui/collapsible";
import { useAssignOwnersMutation } from "../../../data/mutations/assign-owners";

interface AssignOwnersDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

type OwnerMap = Record<string, string | null>;

export function AssignOwnersDialog({ open, onOpenChange }: AssignOwnersDialogProps) {
    const { project } = useProject();
    const currentUser = useCurrentOrgUser();
    const assignOwnersMutation = useAssignOwnersMutation();
    const isSaving = assignOwnersMutation.isPending;

    // Build initial owner map from current project data
    const initialOwners = useMemo(() => {
        const map: OwnerMap = {};
        for (const ws of project.outline.workstreams) {
            map[`ws:${ws.id}`] = ws.ownerId ?? null;
            for (const d of ws.deliverables) {
                map[`d:${ws.id}:${d.id}`] = d.ownerId ?? null;
            }
        }
        return map;
    }, [project]);

    const [owners, setOwners] = useState<OwnerMap>(initialOwners);

    // Reset state when dialog opens
    const handleOpenChange = useCallback(
        (newOpen: boolean) => {
            if (newOpen) {
                setOwners(initialOwners);
            }
            onOpenChange(newOpen);
        },
        [initialOwners, onOpenChange]
    );

    function setOwner(key: string, ownerId: string | null) {
        setOwners((prev) => ({ ...prev, [key]: ownerId }));
    }

    // Count assigned owners
    const totalItems = Object.keys(owners).length;
    const assignedItems = Object.values(owners).filter(Boolean).length;
    const allAssigned = assignedItems === totalItems;
    const progressPercent = totalItems > 0 ? Math.round((assignedItems / totalItems) * 100) : 0;
    const hasChanges = Object.entries(owners).some(([key, val]) => val !== initialOwners[key]);
    const allAssignedToMe = Object.values(owners).every((id) => id === currentUser.id);

    function handleAssignAllToMe() {
        setOwners((prev) => Object.fromEntries(Object.keys(prev).map((key) => [key, currentUser.id])));
    }

    async function handleConfirm() {
        const assignments = Object.entries(owners)
            .filter(([key, ownerId]) => ownerId !== initialOwners[key])
            .map(([key, ownerId]) => {
                if (key.startsWith("ws:")) {
                    return { type: "workstream" as const, workstreamId: key.slice(3), ownerId };
                }
                const [, workstreamId, deliverableId] = key.split(":");
                return { type: "deliverable" as const, workstreamId, deliverableId, ownerId };
            });

        if (assignments.length === 0) {
            onOpenChange(false);
            return;
        }

        await assignOwnersMutation.mutateAsync(assignments);
        onOpenChange(false);
    }

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className={"flex max-h-[85vh] max-w-2xl flex-col gap-0 p-0"}>
                <DialogHeader className={"flex flex-row items-start justify-between gap-4 border-b px-6 py-4"}>
                    <div className={"flex flex-col gap-1"}>
                        <DialogTitle>Assign Owners</DialogTitle>
                        <DialogDescription>{project.name}</DialogDescription>
                        <p className={"text-sm text-muted-foreground"}>
                            Assign an owner to each workstream and all deliverables.
                        </p>
                    </div>
                    <div className={"me-6 flex shrink-0 flex-col items-end gap-1"}>
                        <Badge variant={allAssigned ? "confirmation" : "secondary"}>
                            {assignedItems}/{totalItems} assigned
                        </Badge>
                        <Progress value={progressPercent} color={"confirmation"} className={"h-1.5 w-32"} />
                    </div>
                </DialogHeader>

                <div className={"flex-1 overflow-y-auto"}>
                    {/* Project owner row (read-only) */}
                    <div className={"flex flex-row items-center gap-3 border-b px-8 py-3"}>
                        <div className={"flex-1 font-medium"}>{project.name}</div>
                        <div className={"flex flex-row items-center gap-2 text-sm"}>
                            <UserAvatarAndName
                                avatarFallback={"icon"}
                                userId={project.creatorId}
                                avatarClassName={"w-5 h-5"}
                                nameClassName={"font-medium"}
                            />
                        </div>
                    </div>

                    {/* Workstream + deliverable rows */}
                    {project.outline.workstreams.map((ws, wsIndex) => (
                        <WorkstreamSection
                            key={ws.id}
                            workstreamIndex={wsIndex}
                            workstreamOwnerId={owners[`ws:${ws.id}`] ?? null}
                            onWorkstreamOwnerChange={(ownerId) => setOwner(`ws:${ws.id}`, ownerId)}
                            deliverableOwners={ws.deliverables.map((d) => ({
                                deliverable: d,
                                ownerId: owners[`d:${ws.id}:${d.id}`] ?? null,
                            }))}
                            onDeliverableOwnerChange={(deliverableId, ownerId) =>
                                setOwner(`d:${ws.id}:${deliverableId}`, ownerId)
                            }
                        />
                    ))}
                </div>

                <DialogFooter className={"flex flex-col gap-2 border-t px-6 py-4"}>
                    {assignOwnersMutation.error && (
                        <p className={"text-sm text-destructive"}>{assignOwnersMutation.error.message}</p>
                    )}
                    <div className={"flex w-full flex-row items-center gap-2"}>
                        <Button
                            variant={"outline"}
                            onClick={() => onOpenChange(false)}
                            disabled={isSaving}
                            className={"me-auto"}
                        >
                            Cancel
                        </Button>
                        {!allAssignedToMe && (
                            <Button variant={"outline"} onClick={handleAssignAllToMe} disabled={isSaving}>
                                Assign all to me
                            </Button>
                        )}
                        <Button onClick={handleConfirm} disabled={isSaving || !hasChanges}>
                            {isSaving && <Spinner className={"me-2 h-4 w-4"} />}
                            Confirm
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function WorkstreamSection(props: {
    workstreamIndex: number;
    workstreamOwnerId: string | null;
    onWorkstreamOwnerChange: (ownerId: string | null) => void;
    deliverableOwners: Array<{
        deliverable: { id: string; name: string };
        ownerId: string | null;
    }>;
    onDeliverableOwnerChange: (deliverableId: string, ownerId: string | null) => void;
}) {
    const { project } = useProject();
    const ws = project.outline.workstreams[props.workstreamIndex];

    return (
        <Collapsible defaultOpen>
            <div className={"flex flex-row items-center gap-3 bg-muted/30 px-8 py-4 [[data-state=closed]_&]:border-b"}>
                <CollapsibleTrigger className={"shrink-0"}>
                    <ChevronDownIcon
                        className={
                            "h-4 w-4 text-muted-foreground transition-transform [[data-state=closed]_&]:rotate-[-90deg]"
                        }
                    />
                </CollapsibleTrigger>
                <div className={"flex flex-1 flex-row items-center gap-3"}>
                    <ProjectWorkstreamIcon ws={ws} index={props.workstreamIndex} />
                    <div className={"font-medium"}>{ws.name}</div>
                </div>
                <OwnerSelector
                    ownerId={props.workstreamOwnerId}
                    onOwnerChange={props.onWorkstreamOwnerChange}
                    showChevron
                />
            </div>

            <CollapsibleContent className={"border-b"}>
                {props.deliverableOwners.map(({ deliverable, ownerId }, dIndex) => (
                    <div key={deliverable.id} className={"flex flex-row items-center gap-3 px-8 py-2.5 ps-20"}>
                        <div className={"flex flex-1 flex-row items-center gap-3"}>
                            <DeliverableIcon
                                workstreamIndex={props.workstreamIndex}
                                deliverableIndex={dIndex}
                                size={"xs"}
                            />
                            <div className={"text-sm"}>{deliverable.name}</div>
                        </div>
                        <OwnerSelector
                            ownerId={ownerId}
                            onOwnerChange={(newOwnerId) => props.onDeliverableOwnerChange(deliverable.id, newOwnerId)}
                            showChevron
                        />
                    </div>
                ))}
            </CollapsibleContent>
        </Collapsible>
    );
}

/** Returns true if any workstream or deliverable is missing an owner */
export function hasUnassignedOwners(project: {
    outline: { workstreams: Array<{ ownerId?: string | null; deliverables: Array<{ ownerId?: string | null }> }> };
}): boolean {
    for (const ws of project.outline.workstreams) {
        if (!ws.ownerId) return true;
        for (const d of ws.deliverables) {
            if (!d.ownerId) return true;
        }
    }
    return false;
}
